<template>
  <v-container fluid>
    <v-tabs 
      v-model="currentTabIndex"
      class="mb-3 primary"
    >
      <v-tab
        v-for="tab in tabs"
        :key="tab.key"
      >{{ tab.label }}</v-tab>
    </v-tabs>

    <v-row justify="center" dense>
      <v-btn absolute left icon large color="primary" class="mr-3" @click="fetchAll">
        <v-icon>mdi-reload</v-icon>
      </v-btn>

      <Dialog
        v-if="currentTabKey === 'reports'"
        width="600px"
        :disabled="!isSwayUploadable"
        @apply="uploadSway"
        outlined
      >
        <v-icon class="mr-2">mdi-upload</v-icon>
        アップロード

        <template #title>
          重心動揺データ選択
        </template>

        <template #contents>
          <v-container 
            dense
            fluid 
            @dragleave.prevent 
            @dragover.prevent 
            @drop.prevent="selectFiles"
          >
            <v-row>
              <v-col>
                <v-select
                  label="計測参加者"
                  :items="participants"
                  item-text="name"
                  item-value="id"
                  v-model="participantId"
                />
              </v-col>
            </v-row>
            <v-row 
              dense
              align="center"
            >
              <v-col cols="12">
                <v-file-input
                  accept=".csv"
                  dense
                  hide-details
                  label="計測データ"
                  outlined
                  v-model="swayFile"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  dense
                  v-model="swayName"
                  label="登録名"
                />
              </v-col>
            </v-row>
          </v-container>
        </template>

        <template #apply>アップロード</template>
      </Dialog>

      <Dialog
        width="600px"
        :disabled="!isNewSwayGroupValid"
        @apply="createSwayGroup"
        outlined
        v-if="currentTabKey === 'groups'"
      >
        <v-icon class="mr-2">mdi-account-group</v-icon>
        グループ作成

        <template #title>
          重心動揺グループ作成
        </template>

        <template #contents>
          <v-container 
            dense
            fluid 
            @dragleave.prevent 
            @dragover.prevent 
            @drop.prevent="selectFiles"
          >
            <v-row>
              <v-col>
                <v-text-field
                  v-model="newGroup.name"
                  class="mt-3"
                  dense
                  outlined
                  label="名前"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-textarea
                  v-model="newGroup.description"
                  dense
                  outlined
                  label="説明"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-alert 
              v-if="!newGroup.name"
              type="error"
              dense
              class="mt-3 mb-0"
            >
              名前が未記入です
            </v-alert>
          </v-container>
        </template>

        <template #apply>作成</template>
      </Dialog>
    </v-row>

    <v-row dense>
      <v-col>
        <SwayListTable
          v-if="currentTabKey === 'reports'"
          :loading="isLoading"
          :sway-list="swayList"
          height="calc(100vh - 240px)"
          :items-per-page.sync="itemsPerPage"
          :page.sync="currentPage"
          :filter-params="filterParams"
          class="mt-3"
        />

        <SwayGroupsTable
          v-if="currentTabKey === 'groups'"
          :loading="isLoading"
          :groups="swayGroups"
          height="calc(100vh - 240px)"
          :items-per-page.sync="itemsPerPage"
          :page.sync="currentPage"
          :filter-params="filterParams"
          class="mt-3"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapState } from "vuex";

import SwayGroupsTable from "@components/parts/tables/SwayGroupsTable.vue";
import SwayListTable from "@components/parts/tables/SwayListTable.vue";
import Dialog from "@components/parts/dialogs/Dialog.vue";

export default {
  components: {
    SwayGroupsTable,
    SwayListTable,
    Dialog,
  },
  data: function(){
    return {
      itemsPerPage: -1,
      currentPage: 1,
      isUploading: false,
      participantId: null,
      swayFile: null,
      swayName: null,
      filterParams: {
        isDeleted: false,
      },
      currentTabIndex: 0,
      tabs: [
        {label: 'レポート一覧', key: 'reports'},
        {label: 'グループ一覧', key: 'groups'},
      ],
      newGroup: {
        name: null,
        description: null,
      },
    }
  },
  computed: {
    ...mapState([
      'user',
      'project',
      'participants',
      'swayList',
      'swayGroups',
      'loadingCount',
    ]),
    isNewSwayGroupValid: function(){
      return !!this.newGroup?.name;
    },
    currentTabKey: function(){
      return this.currentTab?.key;
    },
    currentTab: function(){
      return this.tabs[this.currentTabIndex];
    },
    isLoading: function(){
      return this.loadingCount > 0;
    },
    isSwayUploadable: function(){
      if(!this.swayName || !this.swayFile || !this.participantId){
        return false;
      }

      return true;
    },
  },
  watch: {
    swayFile: function(newVal){
      if(newVal){

        if(this.swayName?.length > 0){
          return;
        }

        let swayName = newVal.name.slice(0, -4);
        if(swayName.endsWith('sway')){
          swayName = swayName.slice(0, -('sway'.length));
        }
        this.swayName = swayName;
      }
    },
  },
  destroyed: function(){
  },
  mounted: function(){
    if(!this.project){
      this.$router.push('/');
      return;
    }

    this.fetchAll();
  },
  methods: {
    ...mapActions([
      'fetchParticipants',
      'fetchSwayList',
      'fetchSwayGroups',
    ]),
    fetchAll: function(){
      this.fetchParticipants();
      this.fetchSwayList();
      this.fetchSwayGroups();
    },
    openFileSelection: function(){
      this.$refs.fileInput.click();
    },
    selectFiles: function(e){
      var files = Array.from(e.dataTransfer.files);
      files = files.filter(f => f.name.slice(-4).toLowerCase() === '.csv');
      if(files.length == 0){
        return;
      }

      this.swayFile = files[0];
    },
    createSwayGroup: function(){
      this.axios.post('/api/sway_group', {
        name: this.newGroup?.name,
        description: this.newGroup?.description,
        projectId: this.project.id,
      }).then(() => {
        this.fetchSwayGroups();
      }).catch(error => {
        console.log(error);
      }).finally(() => {
      })
    },
    uploadSway: async function(){
      const reader = new FileReader();
      reader.readAsText(this.swayFile);
      let swayData = [];
      await new Promise(resolve => reader.onload = () => {
        reader.result.split('\n').forEach(l => {
          swayData.push(l.split(','));
        });
        resolve();
      });

      this.axios.post('/api/sway', {
        name: this.swayName,
        sway: swayData,
        projectId: this.project.id,
        participantId: this.participantId,
      }).then(() => {
        this.fetchSwayList();
      }).catch(error => {
        console.log(error);
      }).finally(() => {
      })
    },
  }
}
</script>

<style lang="scss" scoped>

</style>